// Reading a question set from the file a person already has: a text or
// Markdown file, or a sheet with a question column and an answer column. The
// answer is the key: a number as a number, anything else as text, unless a
// `K:` line or a Key column says the kind.
//
// Text shapes, tried in this order:
//   1. `**Q1. question**` header lines, the answer in the lines that follow;
//      or `**Q1.** question`, the question running on to a blank line and the
//      answer after an `Expected:` / `Answer:` / `A:` marker. Where a file
//      marks its answers, the other prose under a question (notes, reference
//      queries) is not the answer.
//   2. `Q:` / `A:` lines (also `Question:` / `Answer:`, any case, numbered or
//      not). An answer runs until a blank line or the next question.
//   3. Alternating paragraphs: a question ending in `?`, a blank line, its
//      answer, a blank line, and so on through the whole file.
//   4. One question per line, with no answers: in a file where any line ends
//      in `?`, only those lines, so prose is never read as a question.
// Fenced code blocks are never questions or answers. An answer read from a
// paragraph becomes the key only when it is a bare value: a number, or a few
// words with no figures in them; a sentence about the answer ("$1,766,127,989
// across 10,009 agreements", "compute at run") is kept as the expected answer
// and the question waits for its key. A `Q:`/`A:` file's `A:` line is the key.
// A Markdown heading that mentions "ontology" marks the questions under it as
// ontology questions; any other heading marks data questions.
import type { EvalQuestion, KeyKind, QuestionType } from "@/lib/api";

const QA_HEADER_RE = /^\*\*\s*Q\d+[.)]?\s*(.*?)\*\*$/i;
// `**Q1.** question text` (or `Q1. question text`): the label alone is bold.
const QA_LABEL_RE = /^(?:\*\*\s*Q\d+[.):]?\s*\*\*|Q\d+[.)])\s+(.+)$/i;
// "Expected:", or "Expected, on the HELM metric:" with a short qualifier.
const ANSWER_MARK_RE =
  /^(?:[-*+]\s+)?\**\s*(?:a|answer|expected answer|expected(?:,[^:]{1,80})?)\s*\d*\s*[:]\s*\**\s*(.*)$/i;
const FENCE_RE = /^(```|~~~)/;
const LIST_MARK_RE = /^(?:[-*+]\s+|\d+[.)]\s+|>\s+)/;
const Q_LINE_RE = /^(?:[-*+]\s+|\d+[.)]\s+)?\**\s*(?:q|question)\s*\d*\s*[:]\s*\**\s*(.*)$/i;
const A_LINE_RE =
  /^(?:[-*+]\s+)?\**\s*(?:a|answer|expected answer)\s*\d*\s*[:]\s*\**\s*(.*)$/i;

const isHr = (l: string) => /^([-*_])\1{2,}$/.test(l);
// Optional lines under a Q:/A: question: `F:` its family, `K:` its key (the
// kind, then the value: `K: list 12312701; 12312702`, `K: nothing recorded`),
// `I:` its id, kept so a re-imported set keeps its history.
const F_LINE_RE = /^(?:[-*+]\s+)?\**\s*(?:f|family)\s*[:]\s*\**\s*(.*)$/i;
const K_LINE_RE = /^(?:[-*+]\s+)?\**\s*(?:k|key)\s*[:]\s*\**\s*(.*)$/i;
const I_LINE_RE = /^(?:[-*+]\s+)?\**\s*(?:i|id)\s*[:]\s*\**\s*(.*)$/i;

/** `K:` text to a key: the kind first, then the value. "nothing recorded" and
 *  "false" take no value; the others need one. Unreadable text is no key. */
export function parseKey(text: string): { key_kind: KeyKind; key_value: string | null } | null {
  const t = text.trim();
  const m = t.match(/^(number|list|text|nothing[ _]recorded|false)\b\s*(.*)$/i);
  if (!m) return null;
  const kindWord = (m[1] ?? "").toLowerCase().replace(" ", "_") as KeyKind;
  const value = (m[2] ?? "").trim();
  if (kindWord === "nothing_recorded" || kindWord === "false") {
    return { key_kind: kindWord, key_value: null };
  }
  if (!value) return null;
  return { key_kind: kindWord, key_value: value };
}

function applyKey(q: EvalQuestion, keyText: string): void {
  const k = parseKey(keyText);
  if (!k) return;
  q.key_kind = k.key_kind;
  q.key_value = k.key_value;
  if (k.key_kind === "nothing_recorded") q.unanswerable = true;
}

const NUMBER_RE = /^-?\d[\d,]*(?:\.\d+)?$/;

/** The answer written as text becomes the key when no key was given: a
 *  number as a number, anything else as text. */
export function keyFromAnswer(q: EvalQuestion): EvalQuestion {
  const answer = q.expected_answer.trim();
  if (q.key_kind || !answer) return q;
  q.key_kind = NUMBER_RE.test(answer) ? "number" : "text";
  q.key_value = answer;
  return q;
}

/** A paragraph answer becomes the key only when it is a bare value: a number
 *  (a leading $ dropped), or one line of at most six words with no figures. */
function keyFromParagraph(q: EvalQuestion): EvalQuestion {
  const answer = q.expected_answer.trim();
  if (q.key_kind || !answer) return q;
  const bare = answer.replace(/^\$/, "").replace(/\.$/, "");
  if (NUMBER_RE.test(bare)) {
    q.key_kind = "number";
    q.key_value = bare;
  } else if (!answer.includes("\n") && answer.split(/\s+/).length <= 6 && !/\d/.test(answer)) {
    q.key_kind = "text";
    q.key_value = answer;
  }
  return q;
}

/** The file with every fenced code block blanked, so a reference query is
 *  never read as a question or an answer; blank lines keep paragraphs apart. */
function withoutCode(lines: string[]): string[] {
  let inside = false;
  return lines.map((l) => {
    if (FENCE_RE.test(l.trim())) {
      inside = !inside;
      return "";
    }
    return inside ? "" : l;
  });
}

function headingKind(l: string): QuestionType | null {
  const m = l.match(/^#{1,6}\s+(.*)$/);
  if (!m) return null;
  return (m[1] ?? "").toLowerCase().includes("ontolog") ? "ontology" : "data";
}

function question(type: QuestionType, text: string, expected = ""): EvalQuestion {
  return {
    id: "",
    type,
    text,
    expected_answer: expected,
    unanswerable: false,
    family: null,
    key_kind: null,
    key_value: null,
  };
}

function parseHeaderBlocks(lines: string[]): EvalQuestion[] {
  // A file that marks its answers keeps only the marked text as each answer.
  const marked = lines.some((l) => ANSWER_MARK_RE.test(l.trim()));
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  let cur: EvalQuestion | null = null;
  // What an unmarked line continues: the question, the answer, or nothing.
  let field: "q" | "a" | null = null;
  const flush = () => {
    if (cur) {
      cur.text = cur.text.trim();
      cur.expected_answer = cur.expected_answer.trim();
      if (cur.text) out.push(cur);
      cur = null;
    }
    field = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (field === "q" || (marked && field === "a")) field = null;
      continue;
    }
    if (isHr(line)) continue;
    const header = line.match(QA_HEADER_RE);
    if (header) {
      flush();
      cur = question(kind, (header[1] ?? "").trim());
      field = marked ? null : "a";
      continue;
    }
    const label = line.match(QA_LABEL_RE);
    if (label) {
      flush();
      cur = question(kind, (label[1] ?? "").trim());
      field = "q";
      continue;
    }
    const hk = headingKind(line);
    if (hk) {
      flush();
      kind = hk;
      continue;
    }
    if (!cur) continue;
    const a = marked ? line.match(ANSWER_MARK_RE) : null;
    if (a) {
      cur.expected_answer += (cur.expected_answer ? " " : "") + (a[1] ?? "");
      field = "a";
      continue;
    }
    const f = line.match(F_LINE_RE);
    if (f) {
      cur.family = (f[1] ?? "").trim() || null;
      continue;
    }
    const k = line.match(K_LINE_RE);
    if (k) {
      applyKey(cur, k[1] ?? "");
      continue;
    }
    const id = line.match(I_LINE_RE);
    if (id) {
      cur.id = (id[1] ?? "").trim();
      continue;
    }
    if (field === "q") cur.text += " " + line;
    else if (field === "a") cur.expected_answer += (cur.expected_answer ? " " : "") + line;
  }
  flush();
  return out;
}

function parseQaLines(lines: string[]): EvalQuestion[] {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  let cur: EvalQuestion | null = null;
  // What a line with no Q:/A: marker continues: the question, the answer, or nothing.
  let field: "q" | "a" | null = null;
  const flush = () => {
    if (cur) {
      cur.text = cur.text.trim();
      cur.expected_answer = cur.expected_answer.trim();
      if (cur.text) out.push(cur);
      cur = null;
    }
    field = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (field === "a") field = null;
      continue;
    }
    if (isHr(line)) continue;
    const hk = headingKind(line);
    if (hk) {
      flush();
      kind = hk;
      continue;
    }
    const q = line.match(Q_LINE_RE);
    if (q) {
      flush();
      cur = question(kind, q[1] ?? "");
      field = "q";
      continue;
    }
    const a = line.match(A_LINE_RE);
    if (a && cur) {
      cur.expected_answer += (cur.expected_answer ? "\n" : "") + (a[1] ?? "");
      field = "a";
      continue;
    }
    const f = line.match(F_LINE_RE);
    if (f && cur) {
      cur.family = (f[1] ?? "").trim() || null;
      field = null;
      continue;
    }
    const k = line.match(K_LINE_RE);
    if (k && cur) {
      applyKey(cur, k[1] ?? "");
      field = null;
      continue;
    }
    const i = line.match(I_LINE_RE);
    if (i && cur) {
      cur.id = (i[1] ?? "").trim();
      field = null;
      continue;
    }
    if (!cur) continue;
    if (field === "q") cur.text += " " + line;
    else if (field === "a") cur.expected_answer += "\n" + line;
  }
  flush();
  return out;
}

// Paragraphs of a file, a heading standing as a paragraph of its own.
function paragraphs(lines: string[]): string[][] {
  const out: string[][] = [];
  let cur: string[] = [];
  const end = () => {
    if (cur.length > 0) out.push(cur);
    cur = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || isHr(line)) {
      end();
      continue;
    }
    if (headingKind(line)) {
      end();
      out.push([line]);
      continue;
    }
    cur.push(line);
  }
  end();
  return out;
}

// Question paragraph, answer paragraph, in turn, under each heading. Only when
// every question paragraph is one line ending in `?` and no answer does, so a
// list of questions split by blank lines is never read as questions and answers.
function parseAlternating(lines: string[]): EvalQuestion[] | null {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  let pending: string | null = null;
  for (const para of paragraphs(lines)) {
    const first = para[0] ?? "";
    const hk = para.length === 1 ? headingKind(first) : null;
    if (hk) {
      if (pending !== null) return null;
      kind = hk;
      continue;
    }
    const text = para.join("\n");
    if (pending === null) {
      if (para.length !== 1 || !first.endsWith("?")) return null;
      pending = first.replace(LIST_MARK_RE, "").trim();
    } else {
      if (para.some((l) => l.endsWith("?"))) return null;
      out.push(question(kind, pending, text));
      pending = null;
    }
  }
  if (pending !== null || out.length === 0) return null;
  return out;
}

function parseOnePerLine(lines: string[]): EvalQuestion[] {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  const asks = (l: string) => l.trim().endsWith("?");
  const onlyAsks = lines.some(asks);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || isHr(line)) continue;
    const hk = headingKind(line);
    if (hk) {
      kind = hk;
      continue;
    }
    if (onlyAsks && !asks(line)) continue;
    const text = line.replace(LIST_MARK_RE, "").trim();
    if (text) out.push(question(kind, text));
  }
  return out;
}

export function parseQuestionsFile(text: string): EvalQuestion[] {
  const lines = withoutCode(text.split(/\r?\n/));
  if (lines.some((l) => QA_HEADER_RE.test(l.trim()) || QA_LABEL_RE.test(l.trim()))) {
    return parseHeaderBlocks(lines).map(keyFromParagraph);
  }
  if (lines.some((l) => Q_LINE_RE.test(l.trim()))) {
    return parseQaLines(lines).map(keyFromAnswer);
  }
  const alternating = parseAlternating(lines);
  return alternating ? alternating.map(keyFromParagraph) : parseOnePerLine(lines);
}

const QUESTION_HEADER_RE = /question/i;
const ANSWER_HEADER_RE = /answer|expected/i;
const TYPE_HEADER_RE = /^\s*(?:type|kind|question type)\s*$/i;
const FAMILY_HEADER_RE = /^\s*family\s*$/i;
const KEY_HEADER_RE = /^\s*key\s*$/i;
const KEY_KIND_HEADER_RE = /^\s*key[ _]?kind\s*$/i;
const KEY_VALUE_HEADER_RE = /^\s*key[ _]?value\s*$/i;
const ID_HEADER_RE = /^\s*(?:id|question id)\s*$/i;

/** Questions from a table: a column headed Question and one headed Answer (or
 *  Expected answer); a column headed Type marks ontology questions. A table
 *  whose header names no question column is read as question, answer in the
 *  first two columns, the header row counted as the first question. */
export function questionsFromTable(columns: string[], rows: string[][]): EvalQuestion[] {
  let qi = columns.findIndex((c) => QUESTION_HEADER_RE.test(c) && !ANSWER_HEADER_RE.test(c));
  let ai = columns.findIndex((c) => ANSWER_HEADER_RE.test(c));
  const ti = columns.findIndex((c) => TYPE_HEADER_RE.test(c));
  const fi = columns.findIndex((c) => FAMILY_HEADER_RE.test(c));
  const ki = columns.findIndex((c) => KEY_HEADER_RE.test(c));
  const kki = columns.findIndex((c) => KEY_KIND_HEADER_RE.test(c));
  const kvi = columns.findIndex((c) => KEY_VALUE_HEADER_RE.test(c));
  const ii = columns.findIndex((c) => ID_HEADER_RE.test(c));
  let body = rows;
  if (qi < 0) {
    qi = 0;
    ai = columns.length > 1 ? 1 : -1;
    body = [columns, ...rows];
  }
  const out: EvalQuestion[] = [];
  for (const row of body) {
    const text = (row[qi] ?? "").trim();
    if (!text) continue;
    const kind: QuestionType =
      ti >= 0 && (row[ti] ?? "").toLowerCase().includes("ontolog") ? "ontology" : "data";
    const q = question(kind, text, ai >= 0 ? (row[ai] ?? "").trim() : "");
    if (fi >= 0) q.family = (row[fi] ?? "").trim() || null;
    if (ii >= 0) q.id = (row[ii] ?? "").trim();
    if (ki >= 0) applyKey(q, row[ki] ?? "");
    else if (kki >= 0) applyKey(q, `${row[kki] ?? ""} ${kvi >= 0 ? (row[kvi] ?? "") : ""}`);
    out.push(keyFromAnswer(q));
  }
  return out;
}

/** The file kinds the bank import reads through the spreadsheet reader. */
export function isSheetFile(name: string): boolean {
  return /\.(csv|xlsx|xls|xlsm|ods)$/i.test(name);
}
